import React, { useState, useEffect } from 'react';
import { ResourceWorkingAvailability, DayAvailability, getResourceWorkingAvailability, updateResourceWorkingAvailability, createResourceWorkingAvailability } from '../../lib/resourceAvailability';
import { Pencil, MapPin } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import ServiceAreasModal from './ServiceAreasModal';
import { useAppSystem } from '../../hooks/useAppSystem';

interface ResourceWorkingAvailabilityProps {
  resourceId: string;
  businessId: string;
  /** Label for the availability toggle (defaults to "Mobile Service"; the setup wizard shows "Online Booking"). */
  toggleLabel?: string;
  /**
   * Hours-only mode: hides the mobile/online-booking toggle and the per-day
   * service-area buttons — just the weekly schedule editor (setup wizard's
   * "set working hours" step).
   */
  scheduleOnly?: boolean;
}

const DEFAULT_AVAILABILITY: Partial<ResourceWorkingAvailability> = {
  monday: { enabled: false, shifts: [] },
  tuesday: { enabled: false, shifts: [] },
  wednesday: { enabled: false, shifts: [] },
  thursday: { enabled: false, shifts: [] },
  friday: { enabled: false, shifts: [] },
  saturday: { enabled: false, shifts: [] },
  sunday: { enabled: false, shifts: [] }
};

const DAYS = [
  { key: 'monday', label: 'Monday' },
  { key: 'tuesday', label: 'Tuesday' },
  { key: 'wednesday', label: 'Wednesday' },
  { key: 'thursday', label: 'Thursday' },
  { key: 'friday', label: 'Friday' },
  { key: 'saturday', label: 'Saturday' },
  { key: 'sunday', label: 'Sunday' }
];

const ResourceWorkingAvailabilityComponent: React.FC<ResourceWorkingAvailabilityProps> = ({ resourceId, businessId, toggleLabel = 'Mobile Service', scheduleOnly = false }) => {
  const { coreApps, installedAddons } = useAppSystem();
  const [availability, setAvailability] = useState<ResourceWorkingAvailability | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState<ResourceWorkingAvailability | null>(null);
  const [mobileAvailable, setMobileAvailable] = useState(false);
  const [mobileLoading, setMobileLoading] = useState(true);
  const [selectedDay, setSelectedDay] = useState<string | null>(null);

  const [isAreasModalOpen, setIsAreasModalOpen] = useState(false);
  const [currentAreas, setCurrentAreas] = useState<string[]>([]);

  useEffect(() => {
    // Reset all state when resource changes
    console.log("Resource changed to:", resourceId);
    setMobileAvailable(false); // Reset to default false state
    setMobileLoading(true);    // Show loading state
    setAvailability(null);     // Clear availability data
    setLoading(true);          // Show loading state
    setError(null);            // Clear any errors
    setEditMode(false);        // Exit edit mode
    setFormData(null);         // Clear form data
    setCurrentAreas([]);       // Clear areas
    
    // Then fetch new data for this resource
    fetchAvailability();
    fetchMobileAvailability();
  }, [resourceId]);

  const fetchMobileAvailability = async () => {
    // Keep track of the current resourceId to prevent race conditions
    const currentResourceId = resourceId;
    
    if (!supabase) {
      console.error('Supabase client is not initialized');
      setMobileAvailable(false);
      setMobileLoading(false);
      return;
    }

    try {
      console.log(`[${currentResourceId}] Checking mobile availability...`);
      setMobileLoading(true);
      
      // Check if a record exists for this resource directly from the mobile_availability table
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('id, mobile_available')
        .eq('resource_id', currentResourceId)
        .maybeSingle();
      
      // Skip processing if resource has changed during the request
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during request, aborting`);
        return;
      }
      
      console.log(`[${currentResourceId}] Mobile availability check result:`, { 
        found: !!data, 
        mobile_available: data?.mobile_available 
      });
      
      if (error && error.code !== 'PGRST116') {
        console.error(`[${currentResourceId}] Error fetching mobile availability:`, error);
        throw error;
      }
      
      // If we found a record in mobile_availability table, set to true regardless of mobile_available field
      // This is because the existence of the record in this table means the resource should be mobile available
      if (data) {
        console.log(`[${currentResourceId}] Found mobile availability record, setting to TRUE`);
        setMobileAvailable(true);
      } else {
        console.log(`[${currentResourceId}] No mobile availability record found, setting to FALSE`);
        setMobileAvailable(false);
      }
    } catch (err) {
      // Skip error handling if resource has changed
      if (currentResourceId !== resourceId) {
        return;
      }
      
      console.error(`[${currentResourceId}] Error fetching mobile availability:`, err);
      // Set to false if there's any error
      setMobileAvailable(false);
    } finally {
      // Skip state update if resource has changed
      if (currentResourceId !== resourceId) {
        return;
      }
      
      setMobileLoading(false);
    }
  };

  const handleMobileToggle = async () => {
    if (!supabase || !availability) {
      console.error('Supabase client is not initialized or availability data is missing');
      return;
    }

    // Store the current resource ID to prevent race conditions
    const currentResourceId = resourceId;
    const newValue = !mobileAvailable;
    
    console.log(`[${currentResourceId}] Toggling mobile availability to: ${newValue}`);
    
    try {
      setMobileLoading(true);
      
      if (newValue) {
        // ENABLING MOBILE AVAILABILITY
        console.log(`[${currentResourceId}] Enabling mobile availability`);
        
        // Get resource details to get first and last name
        const { data: resourceData, error: resourceError } = await supabase
          .from('resources')
          .select('first_name, last_name')
          .eq('id', currentResourceId)
          .single();

        // Abort if resource changed during the request
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }

        if (resourceError) {
          console.error(`[${currentResourceId}] Error fetching resource data:`, resourceError);
          throw resourceError;
        }

        // Create working_hours object from current availability
        const working_hours = {
          monday: {
            enabled: availability.monday.enabled,
            radius: 0,
            shifts: availability.monday.shifts,
            areas: []
          },
          tuesday: {
            enabled: availability.tuesday.enabled,
            radius: 0,
            shifts: availability.tuesday.shifts,
            areas: []
          },
          wednesday: {
            enabled: availability.wednesday.enabled,
            radius: 0,
            shifts: availability.wednesday.shifts,
            areas: []
          },
          thursday: {
            enabled: availability.thursday.enabled,
            radius: 0,
            shifts: availability.thursday.shifts,
            areas: []
          },
          friday: {
            enabled: availability.friday.enabled,
            radius: 0,
            shifts: availability.friday.shifts,
            areas: []
          },
          saturday: {
            enabled: availability.saturday.enabled,
            radius: 0,
            shifts: availability.saturday.shifts,
            areas: []
          },
          sunday: {
            enabled: availability.sunday.enabled,
            radius: 0,
            shifts: availability.sunday.shifts,
            areas: []
          }
        };

        // First try to delete any existing record to avoid conflicts
        await supabase
          .from('mobile_availability')
          .delete()
          .eq('resource_id', currentResourceId);
        
        // Abort if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }
          
        console.log(`[${currentResourceId}] Deleted any existing mobile_availability records`);

        // Create new record to ensure consistency
        console.log(`[${currentResourceId}] Creating fresh mobile_availability record`);
        const { error: insertError } = await supabase
          .from('mobile_availability')
          .insert({
            resource_id: currentResourceId,
            business_id: businessId,
            mobile_available: true, // Always set to true when explicitly enabled
            working_hours,
            first_name: resourceData.first_name,
            last_name: resourceData.last_name,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          });

        // Abort if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }

        if (insertError) {
          console.error(`[${currentResourceId}] Error creating mobile availability record:`, insertError);
          throw insertError;
        }
        
        // Update the resources table to set is_mobile_available = true
        const { error: updateResourceError } = await supabase
          .from('resources')
          .update({ is_mobile_available: true })
          .eq('id', currentResourceId);
        
        if (updateResourceError) {
          console.error(`[${currentResourceId}] Error updating resource is_mobile_available:`, updateResourceError);
          // Don't throw - the mobile_availability record was created successfully
        } else {
          console.log(`[${currentResourceId}] Successfully updated resource is_mobile_available to true`);
        }
        
        console.log(`[${currentResourceId}] Successfully enabled mobile availability`);
      } else {
        // DISABLING MOBILE AVAILABILITY
        console.log(`[${currentResourceId}] Disabling mobile availability`);
        
        // When disabling, just delete the record completely
        const { error: deleteError } = await supabase
          .from('mobile_availability')
          .delete()
          .eq('resource_id', currentResourceId);

        // Abort if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during request, aborting toggle operation`);
          return;
        }

        if (deleteError) {
          console.error(`[${currentResourceId}] Error deleting mobile availability record:`, deleteError);
          throw deleteError;
        }
        
        // Update the resources table to set is_mobile_available = false
        const { error: updateResourceError } = await supabase
          .from('resources')
          .update({ is_mobile_available: false })
          .eq('id', currentResourceId);
        
        if (updateResourceError) {
          console.error(`[${currentResourceId}] Error updating resource is_mobile_available:`, updateResourceError);
          // Don't throw - the mobile_availability record was deleted successfully
        } else {
          console.log(`[${currentResourceId}] Successfully updated resource is_mobile_available to false`);
        }
        
        console.log(`[${currentResourceId}] Successfully disabled mobile availability by deleting record`);
      }

      // Skip remaining operations if resource has changed
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during operation, skipping final updates`);
        return;
      }

      // Update UI state
      setMobileAvailable(newValue);
      
      // Force refresh data from the database
      await fetchMobileAvailability();
      
      // Refresh availability data to get areas information
      if (newValue) {
        await fetchAvailability();
      }
      
    } catch (err) {
      // Skip error handling if resource has changed
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during operation, skipping error handling`);
        return;
      }
      
      console.error(`[${currentResourceId}] Error updating mobile availability:`, err);
      // Revert the UI state if there was an error
      setMobileAvailable(!newValue);
      // Force refresh data from the database
      await fetchMobileAvailability();
    } finally {
      // Skip final cleanup if resource has changed
      if (currentResourceId !== resourceId) {
        return;
      }
      
      setMobileLoading(false);
    }
  };

  const fetchAvailability = async () => {
    // Track current resource to prevent race conditions
    const currentResourceId = resourceId;
    
    try {
      console.log(`[${currentResourceId}] Fetching availability for resource`);
      setLoading(true);
      setError(null);
      
      // Clear existing data first to prevent displaying stale data
      setAvailability(null);
      setFormData(null);
      
      let data = await getResourceWorkingAvailability(currentResourceId);
      
      // Abandon operation if resource changed
      if (currentResourceId !== resourceId) {
        console.log(`[${currentResourceId}] Resource changed during availability fetch, aborting`);
        return;
      }
      
      console.log(`[${currentResourceId}] Availability data fetched:`, data ? 'Data found' : 'No data found');
      
      if (!data) {
        console.log(`[${currentResourceId}] No availability data found, creating default`);
        await createResourceWorkingAvailability(currentResourceId, businessId, DEFAULT_AVAILABILITY);
        data = await getResourceWorkingAvailability(currentResourceId);
        
        // Abandon operation if resource changed
        if (currentResourceId !== resourceId) {
          console.log(`[${currentResourceId}] Resource changed during availability creation, aborting`);
          return;
        }
        
        if (!data) {
          throw new Error('Failed to initialize availability data');
        }
      }

      // Fetch mobile availability data to get areas information
      if (supabase) {
        try {
          const { data: mobileData, error: mobileError } = await supabase
            .from('mobile_availability')
            .select('working_hours')
            .eq('resource_id', currentResourceId)
            .maybeSingle();

          // Abandon operation if resource changed
          if (currentResourceId !== resourceId) {
            console.log(`[${currentResourceId}] Resource changed during mobile data fetch, aborting`);
            return;
          }

          if (!mobileError && mobileData?.working_hours) {
            console.log(`[${currentResourceId}] Merging areas data from mobile_availability`);
            // Merge areas data into the availability data
            const days = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'] as const;
            
            days.forEach(day => {
              if (data[day] && mobileData.working_hours[day]?.areas) {
                (data[day] as any).areas = mobileData.working_hours[day].areas;
              }
            });
          }
        } catch (mobileErr) {
          console.log(`[${currentResourceId}] Could not fetch mobile data (this is ok):`, mobileErr);
          // This is not a critical error - continue without areas data
        }
      }
      
      // Log resource availability to debug
      console.log(`[${currentResourceId}] Setting availability data with days:`, 
        Object.keys(data).map(day => {
          const dayKey = day as keyof ResourceWorkingAvailability;
          const dayData = data[dayKey] as DayAvailability;
          const areasCount = (dayData as any)?.areas?.length || 0;
          return `${day}: ${dayData.enabled ? 'enabled' : 'disabled'}${areasCount > 0 ? ` (${areasCount} areas)` : ''}`;
        }).join(', ')
      );
      
      // Only update state if we're still looking at the same resource
      if (currentResourceId === resourceId) {
        setAvailability(data);
        setFormData(data);
      } else {
        console.log(`[${currentResourceId}] Resource changed, not updating state`);
      }
    } catch (err) {
      // Only update error state if we're still on the same resource
      if (currentResourceId === resourceId) {
        console.error(`[${currentResourceId}] Error fetching availability:`, err);
        setError(err instanceof Error ? err.message : 'Failed to load availability data');
      }
    } finally {
      // Only update loading state if we're still on the same resource
      if (currentResourceId === resourceId) {
        setLoading(false);
      }
    }
  };

  const handleSave = async () => {
    if (!formData) return;
    
    try {
      setLoading(true);
      
      // First update the resource's working availability
      await updateResourceWorkingAvailability(resourceId, formData);
      console.log('Updated resource working availability');
      
      // Check if this resource has mobile availability enabled
      if (mobileAvailable && supabase) {
        console.log('Resource has mobile availability, syncing working hours');
        
        // Check if a record exists in mobile_availability
        const { data: existingRecord } = await supabase
          .from('mobile_availability')
          .select('id, mobile_available, first_name, last_name, working_hours')
          .eq('resource_id', resourceId)
          .maybeSingle();
          
        // Convert formData to mobile availability working hours format
        const working_hours = {
          monday: {
            enabled: formData.monday.enabled,
            shifts: formData.monday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.monday?.areas || []
          },
          tuesday: {
            enabled: formData.tuesday.enabled,
            shifts: formData.tuesday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.tuesday?.areas || []
          },
          wednesday: {
            enabled: formData.wednesday.enabled,
            shifts: formData.wednesday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.wednesday?.areas || []
          },
          thursday: {
            enabled: formData.thursday.enabled,
            shifts: formData.thursday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.thursday?.areas || []
          },
          friday: {
            enabled: formData.friday.enabled,
            shifts: formData.friday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.friday?.areas || []
          },
          saturday: {
            enabled: formData.saturday.enabled,
            shifts: formData.saturday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.saturday?.areas || []
          },
          sunday: {
            enabled: formData.sunday.enabled,
            shifts: formData.sunday.shifts,
            radius: 0,
            areas: existingRecord?.working_hours?.sunday?.areas || []
          }
        };
        
        if (existingRecord) {
          // Update existing record
          console.log('Updating existing mobile_availability record');
          const { error: updateError } = await supabase
            .from('mobile_availability')
            .update({ 
              working_hours,
              mobile_available: true, // Always set to true when a record exists
              updated_at: new Date().toISOString()
            })
            .eq('resource_id', resourceId);
  
          if (updateError) {
            console.error('Error updating mobile availability:', updateError);
            throw updateError;
          }
        } else {
          // Need to create a new record - get resource details first
          console.log('No mobile_availability record exists, creating new one');
          const { data: resourceData, error: resourceError } = await supabase
            .from('resources')
            .select('first_name, last_name')
            .eq('id', resourceId)
            .single();
  
          if (resourceError) {
            console.error('Error fetching resource data:', resourceError);
            throw resourceError;
          }
          
          // Create new mobile_availability record
          const { error: insertError } = await supabase
            .from('mobile_availability')
            .insert({
              resource_id: resourceId,
              business_id: businessId,
              mobile_available: true,
              working_hours,
              first_name: resourceData.first_name,
              last_name: resourceData.last_name,
              created_at: new Date().toISOString(),
              updated_at: new Date().toISOString()
            });
  
          if (insertError) {
            console.error('Error creating mobile availability record:', insertError);
            throw insertError;
          }
        }
        
        console.log('Successfully synchronized mobile availability working hours');
      }

      // Update local state
      setAvailability(formData);
      setEditMode(false);
      
      // Refresh mobile availability status
      await fetchMobileAvailability();
      
    } catch (err) {
      console.error('Error saving availability:', err);
      setError(err instanceof Error ? err.message : 'Failed to save availability data');
      
      // Refresh mobile availability status
      await fetchMobileAvailability();
    } finally {
      setLoading(false);
    }
  };

  const handleToggleDay = (dayKey: keyof ResourceWorkingAvailability) => {
    if (!formData) return;

    const dayAvailability = formData[dayKey] as DayAvailability;
    setFormData({
      ...formData,
      [dayKey]: {
        enabled: !dayAvailability.enabled,
        shifts: dayAvailability.enabled ? [] : [{ start: '09:00', end: '17:00' }]
      }
    });
  };

  const handleShiftChange = (dayKey: keyof ResourceWorkingAvailability, start: string, end: string) => {
    if (!formData) return;

    setFormData({
      ...formData,
      [dayKey]: {
        enabled: true,
        shifts: [{ start, end }]
      }
    });
  };

  const fetchCurrentAreas = async (day: string) => {
    if (!supabase) return;

    try {
      const { data, error } = await supabase
        .from('mobile_availability')
        .select('working_hours')
        .eq('resource_id', resourceId)
        .single();

      if (error) {
        console.error('Error fetching current areas:', error);
        return;
      }

      const dayKey = day.toLowerCase();
      const areas = data?.working_hours?.[dayKey]?.areas || [];
      setCurrentAreas(areas);
    } catch (err) {
      console.error('Error fetching current areas:', err);
    }
  };

  const handleAreasClick = async (day: string) => {
    setSelectedDay(day);
    await fetchCurrentAreas(day);
    setIsAreasModalOpen(true);
  };

  const handleSaveAreas = async (selectedAreas: string[]) => {
    if (!supabase || !selectedDay) return;

    try {
      console.log('Saving areas:', { selectedAreas, selectedDay });
      setMobileLoading(true);

      // First get current mobile availability data
      const { data: currentData } = await supabase
        .from('mobile_availability')
        .select('working_hours, postal_codes')
        .eq('resource_id', resourceId)
        .single();

      // Get the selected service areas' postal codes
      const { data: serviceAreas, error: serviceAreasError } = await supabase
        .from('service_areas')
        .select('*')
        .in('id', selectedAreas);

      if (serviceAreasError) {
        console.error('Error fetching service areas:', serviceAreasError);
        throw serviceAreasError;
      }

      // Combine all postal codes from selected service areas
      const allPostalCodes = serviceAreas?.reduce((acc: string[], area) => {
        return [...acc, ...(area.postal_codes || [])];
      }, []) || [];

      console.log('Service areas data:', serviceAreas);
      console.log('Combined postal codes:', allPostalCodes);

      // Initialize or get existing working_hours
      const working_hours = currentData?.working_hours || {
        monday: { enabled: false, shifts: [], areas: [] },
        tuesday: { enabled: false, shifts: [], areas: [] },
        wednesday: { enabled: false, shifts: [], areas: [] },
        thursday: { enabled: false, shifts: [], areas: [] },
        friday: { enabled: false, shifts: [], areas: [] },
        saturday: { enabled: false, shifts: [], areas: [] },
        sunday: { enabled: false, shifts: [], areas: [] }
      };

      // Update working_hours for the selected day
      const dayKey = selectedDay.toLowerCase() as keyof ResourceWorkingAvailability;
      working_hours[dayKey] = {
        ...working_hours[dayKey],
        areas: selectedAreas,
        enabled: true,
        shifts: working_hours[dayKey]?.shifts || [{ start: '09:00', end: '17:00' }]
      };

      // Initialize or get existing postal_codes
      const postal_codes = {
        ...(currentData?.postal_codes || {}),
        [dayKey]: allPostalCodes
      };

      console.log('Updating with:', {
        working_hours,
        postal_codes,
        dayKey,
        selectedAreas
      });

      // First delete any existing record
      await supabase
        .from('mobile_availability')
        .delete()
        .eq('resource_id', resourceId);

      // Then insert the new record
      const { error: insertError } = await supabase
        .from('mobile_availability')
        .insert({
          resource_id: resourceId,
          business_id: businessId,
          mobile_available: true,
          working_hours,
          postal_codes,
          updated_at: new Date().toISOString()
        });

      if (insertError) {
        console.error('Error inserting mobile availability:', insertError);
        throw insertError;
      }

      // Update the local state with the new working hours
      if (availability) {
        const dayKey = selectedDay.toLowerCase() as keyof ResourceWorkingAvailability;
        const currentDay = availability[dayKey] as DayAvailability;
        const updatedAvailability = {
          ...availability,
          [dayKey]: {
            enabled: currentDay.enabled,
            shifts: currentDay.shifts,
            areas: selectedAreas
          }
        };
        setAvailability(updatedAvailability);
      }

      // Update current areas state
      setCurrentAreas(selectedAreas);

      console.log('Successfully updated mobile availability');
      setIsAreasModalOpen(false);
    } catch (err) {
      console.error('Error updating service areas:', err);
    } finally {
      setMobileLoading(false);
    }
  };

  if (loading) {
    return <div className="p-4 text-center">Loading availability...</div>;
  }

  if (error) {
    return <div className="p-4 text-red-500">{error}</div>;
  }

  if (!availability) {
    return <div className="p-4 text-center">No availability data found</div>;
  }

  return (
    <div className="space-y-0">
      {editMode && (
        <div className="px-2 py-2 sm:px-6 sm:py-3 border-b border-purple-200/30 bg-purple-50/30 flex justify-end items-center space-x-2">
          <button
            onClick={() => {
              setFormData(availability);
              setEditMode(false);
            }}
            className="inline-flex items-center justify-center px-3 py-2 text-xs font-semibold text-gray-700 bg-gray-100/80 backdrop-blur-xl rounded-xl hover:bg-gray-200/80 transition-all duration-300 ease-out transform hover:scale-105 active:scale-95 shadow-sm touch-manipulation"
            style={{ 
              minHeight: '32px',
              backdropFilter: 'blur(10px)',
              WebkitBackdropFilter: 'blur(10px)'
            }}
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={loading}
            className="px-3 py-1.5 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
          >
            {loading ? (
              <>
                <div className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin mr-2"></div>
                Saving...
              </>
            ) : (
              'Save'
            )}
          </button>
        </div>
      )}

      {/* Mobile Service Section (hours-only mode swaps it for a plain header) */}
      {scheduleOnly ? (
        <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-gray-200/60 bg-gray-50/50 flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Pencil className="h-4 w-4 text-gray-500" />
            <span className="text-xs sm:text-sm font-semibold text-gray-700">Working Hours</span>
          </div>
          {!editMode && (
            <button
              onClick={() => {
                setFormData(availability);
                setEditMode(true);
              }}
              className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-gray-100/50 hover:bg-gray-200/50 text-gray-600 hover:text-gray-700 transition-all duration-200 ease-out transform hover:scale-105 active:scale-95 touch-manipulation"
            >
              <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          )}
        </div>
      ) : (
        <div className="px-2 py-1.5 sm:px-6 sm:py-4 border-b border-purple-200/30 bg-purple-50/30 flex items-center justify-between">
        <div className="flex items-center space-x-2">
          <MapPin className="h-4 w-4 text-gray-500" />
          <span className="text-xs sm:text-sm font-semibold text-gray-700">{toggleLabel}</span>
        </div>
        <div className="flex items-center space-x-2 sm:space-x-3">
          <button
            onClick={handleMobileToggle}
            disabled={mobileLoading}
            className={`
              relative inline-flex h-5 w-9 sm:h-6 sm:w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
              transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:ring-offset-2
              ${mobileAvailable ? 'bg-blue-600' : 'bg-gray-300'}
              ${mobileLoading ? 'opacity-50 cursor-not-allowed' : ''}
            `}
            role="switch"
            aria-checked={mobileAvailable}
          >
            <span className="sr-only">Enable mobile service</span>
            <span
              className={`
                pointer-events-none inline-block h-4 w-4 sm:h-5 sm:w-5 transform rounded-full bg-white shadow ring-0 
                transition duration-200 ease-in-out
                ${mobileAvailable ? 'translate-x-4 sm:translate-x-5' : 'translate-x-0'}
              `}
            />
          </button>
          <span className="text-xs sm:text-sm text-gray-600 font-medium">
            {mobileLoading ? 'Loading...' : (mobileAvailable ? 'Enabled' : 'Disabled')}
          </span>
          {!editMode && (
            <button
              onClick={() => {
                setFormData(availability);
                setEditMode(true);
              }}
              className="flex items-center justify-center w-6 h-6 sm:w-8 sm:h-8 rounded-lg bg-gray-100/50 hover:bg-gray-200/50 text-gray-600 hover:text-gray-700 transition-all duration-200 ease-out transform hover:scale-105 active:scale-95 touch-manipulation ml-1 sm:ml-2"
            >
              <Pencil className="h-3 w-3 sm:h-4 sm:w-4" />
            </button>
          )}
        </div>
      </div>
      )}

      <div className="p-2 sm:p-6">
        <div className="space-y-4">
          {DAYS.map(({ key, label }) => {
            const dayAvailability = (editMode ? formData : availability)?.[key as keyof ResourceWorkingAvailability] as DayAvailability;
            
            // Check if day has hours but no areas (when mobile service is enabled)
            const hasHours = dayAvailability.enabled && dayAvailability.shifts && dayAvailability.shifts.length > 0;
            const hasAreas = dayAvailability?.areas && Array.isArray(dayAvailability.areas) && dayAvailability.areas.length > 0;
            const needsAreas = !scheduleOnly && mobileAvailable && hasHours && !hasAreas;
            
            return (
              <div 
                key={key} 
                className={`flex items-center justify-between p-3 rounded-lg transition-colors ${
                  needsAreas 
                    ? 'bg-red-50 border-2 border-red-200' 
                    : ''
                }`}
              >
                <div className={`w-24 text-sm capitalize ${needsAreas ? 'text-red-700 font-semibold' : 'text-gray-600'}`}>
                  {label}
                </div>
                {editMode ? (
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center space-x-2">
                      <input
                        type="checkbox"
                        checked={dayAvailability.enabled}
                        onChange={() => handleToggleDay(key as keyof ResourceWorkingAvailability)}
                        className="h-4 w-4 rounded border-gray-300 text-gray-900 focus:ring-gray-900"
                      />
                      <span className="text-sm text-gray-600">Available</span>
                    </label>
                    {dayAvailability.enabled && (
                      <div className="flex items-center space-x-2">
                        <input
                          type="time"
                          value={dayAvailability.shifts[0]?.start || '09:00'}
                          onChange={(e) => handleShiftChange(key as keyof ResourceWorkingAvailability, e.target.value, dayAvailability.shifts[0]?.end || '17:00')}
                          className="w-24 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                        <span className="text-sm text-gray-500">to</span>
                        <input
                          type="time"
                          value={dayAvailability.shifts[0]?.end || '17:00'}
                          onChange={(e) => handleShiftChange(key as keyof ResourceWorkingAvailability, dayAvailability.shifts[0]?.start || '09:00', e.target.value)}
                          className="w-24 h-9 rounded-md border-gray-300 shadow-sm focus:border-gray-900 focus:ring-gray-900 text-sm"
                        />
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm flex flex-col sm:flex-row sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                    {dayAvailability.enabled ? (
                      <>
                        <div className={`flex items-center space-x-2 ${needsAreas ? 'text-red-700' : 'text-gray-900'}`}>
                          <span className={needsAreas ? 'text-red-600 font-semibold' : 'text-green-600'}>Available</span>
                          <span className={needsAreas ? 'text-red-600' : 'text-gray-500'}>
                            {dayAvailability.shifts[0]?.start} - {dayAvailability.shifts[0]?.end}
                          </span>
                        </div>
                        {!scheduleOnly && mobileAvailable && (
                          <div className="flex flex-col sm:flex-row sm:items-center space-y-1.5 sm:space-y-0 sm:space-x-2">
                            <button
                              onClick={() => handleAreasClick(label)}
                              className={`px-2 py-1 text-xs font-medium border rounded-md transition-colors duration-200 flex items-center self-start ${
                                needsAreas
                                  ? 'text-red-700 bg-red-50 border-red-300 hover:bg-red-100 hover:border-red-400'
                                  : 'text-blue-600 hover:text-blue-700 border-blue-200 hover:border-blue-300'
                              }`}
                            >
                              <span>Areas</span>
                              {dayAvailability?.areas && dayAvailability.areas.length > 0 && (
                                <span className={`ml-1.5 inline-flex items-center justify-center text-xs font-semibold px-1.5 py-0.5 rounded-full ${
                                  needsAreas
                                    ? 'bg-red-200 text-red-800'
                                    : 'bg-blue-100 text-blue-800'
                                }`}>
                                  {dayAvailability.areas.length}
                                </span>
                              )}
                              {needsAreas && (
                                <span className="ml-1.5 text-red-600 font-semibold">⚠</span>
                              )}
                            </button>
                            {needsAreas && (
                              <span className="text-xs font-medium text-red-600 sm:whitespace-nowrap">
                                No service areas set
                              </span>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <span className="text-gray-500">Not available</span>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {isAreasModalOpen && selectedDay && (
        <ServiceAreasModal
          isOpen={isAreasModalOpen}
          onClose={() => setIsAreasModalOpen(false)}
          businessId={businessId}
          day={selectedDay}
          onSave={handleSaveAreas}
          currentAreas={currentAreas}
        />
      )}
    </div>
  );
};

// Wrap with a keyed component to force remounting when resourceId changes
const KeyedResourceWorkingAvailability: React.FC<ResourceWorkingAvailabilityProps> = (props) => {
  // Use resourceId as a key to ensure complete component remount when resource changes
  return <ResourceWorkingAvailabilityComponent key={props.resourceId} {...props} />;
};

export default KeyedResourceWorkingAvailability; 